import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { serverUrl, getAuthHeader } from ".";
import { DocStatus } from "../types/docs";
import { PaginationType } from "../types/filter";

export const useDocsFilenames = () => {
  return useQuery({
    queryKey: ["docs"],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/documents/filenames`,
        getAuthHeader()
      );
      return response.data;
    },
  });
};

export const useDirectoryFilenames = ({
  directory,
  department,
  enabled,
}: {
  directory: string;
  department?: string;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: ["docs", directory, department],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/documents/${directory}/filenames${
          department ? `?department=${department}` : ""
        }`,
        getAuthHeader()
      );
      return response.data;
    },
    enabled,
  });
};

export const useUploadDocs = ({ directory }: { directory: string }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      filename,
      files,
    }: {
      filename: string;
      files: File[];
    }) => {
      const formData = new FormData();
      formData.append("metadata", JSON.stringify({ directory, filename }));
      Array.from(files).forEach((file) => {
        formData.append("files", file);
      });
      const response = await axios.post(
        `${serverUrl}/documents/upload`,
        formData,
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ownDocs", { directory }] });
      alert("성공적으로 파일을 업로드했습니다.");
    },
  });
};

export const useDocsDirectories = () => {
  return useQuery({
    queryKey: ["docsDirectories"],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/documents/dirs`,
        getAuthHeader()
      );
      return response.data;
    },
  });
};

export const useDocsStatus = ({
  directory,
  filters,
  pagination,
}: {
  directory: string;
  filters: Record<string, string> | undefined;
  pagination: PaginationType;
}) => {
  return useQuery({
    queryKey: ["docsStatus", directory, filters, pagination],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/documents/${directory}/status?${new URLSearchParams({
          filters: JSON.stringify(filters),
          start: (pagination.page * pagination.size).toString(),
          limit: pagination.size.toString(),
        }).toString()}`,
        getAuthHeader()
      );
      return response.data;
    },
    refetchOnMount: false,
  });
};

export const useFile = (link: string) => {
  return useQuery({
    queryKey: ["file", link],
    queryFn: async () => {
      const response = await axios.get(
        `${serverUrl}/documents/file/${encodeURI(link)}`,
        getAuthHeader()
      );
      return response;
    },
  });
};

export const useUpdateStatus = ({
  studentId,
  fileId,
}: {
  studentId: number;
  fileId: number;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      status,
      comment,
    }: {
      status: Exclude<DocStatus, "">;
      comment: string;
    }) => {
      const response = await axios.post(
        `${serverUrl}/documents/status`,
        {
          file_id: fileId,
          student_id: studentId,
          status,
          comment,
        },
        getAuthHeader()
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["docsStatus"] });
      alert("성공적으로 상태를 업데이트했습니다.");
    },
  });
};

export const getScoreXlsx = async () => {
  return await axios({
    method: "GET",
    url: `${serverUrl}/documents/excel`,
    responseType: "blob",
    headers: getAuthHeader().headers,
  });
};
